/**
 * MessageBubble — renders a single chat message.
 *
 * For user messages: right-aligned blue bubble.
 * For assistant messages: left-aligned with AI avatar, markdown rendering,
 * and a collapsible "reasoning" (thinking) section when present.
 */

import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage, ToolCall } from '../../store/CockpitStore';
import { useThemeClasses } from '../../providers/ThemeProvider';
import { cx } from '../../styles';

// ─── Markdown prose styles ───────────────────────────────────────────────────

const PROSE_CLASSES = [
  '[&_table]:w-full [&_table]:border-collapse [&_table]:my-2 [&_table]:text-sm',
  '[&_th]:text-left [&_th]:px-2 [&_th]:py-1 [&_th]:font-semibold [&_th]:border-b [&_th]:border-slate-300 dark:[&_th]:border-slate-600',
  '[&_td]:px-2 [&_td]:py-1 [&_td]:border-b [&_td]:border-slate-200 dark:[&_td]:border-slate-700',
  '[&_pre]:rounded-lg [&_pre]:p-3 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:text-xs [&_pre]:bg-gray-100 dark:[&_pre]:bg-slate-900',
  '[&_code]:text-xs [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:bg-gray-100 dark:[&_code]:bg-slate-900',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1',
  '[&_li]:my-0.5 [&_li]:text-sm',
  '[&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_p]:text-sm [&_p]:leading-relaxed',
  '[&_h1]:text-base [&_h1]:font-bold [&_h1]:my-2',
  '[&_h2]:text-sm [&_h2]:font-bold [&_h2]:my-1.5',
  '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:my-1',
  '[&_strong]:font-semibold',
  '[&_a]:text-blue-500 [&_a]:underline',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-blue-300 [&_blockquote]:pl-3 [&_blockquote]:my-2 [&_blockquote]:text-sm [&_blockquote]:opacity-80',
].join(' ');

// ─── Reasoning section ──────────────────────────────────────────────────────

const ReasoningBlock: React.FC<{ text: string; isStreaming?: boolean }> = ({
  text,
  isStreaming,
}) => {
  const [expanded, setExpanded] = useState(!!isStreaming);
  const tc = useThemeClasses();

  // Auto-expand when streaming starts, auto-collapse when response text begins
  useEffect(() => {
    setExpanded(!!isStreaming);
  }, [isStreaming]);

  if (!text) return null;

  return (
    <div className={cx('mb-2 rounded-lg border', tc.borderPrimary, 'overflow-hidden')}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cx(
          'w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors',
          tc.bgTertiary,
          tc.textMuted,
          tc.hover,
        )}
      >
        {/* Thinking indicator */}
        {isStreaming ? (
          <span className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>
            Thinking...
          </span>
        ) : (
          <span className="flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
              <path d="M8 1a4.75 4.75 0 00-1.605 9.222.75.75 0 01.505.712v1.316a.75.75 0 00.75.75h.7a.75.75 0 00.75-.75v-1.316a.75.75 0 01.505-.712A4.75 4.75 0 008 1z" />
              <path d="M6.75 14.5a.75.75 0 01.75-.75h1a.75.75 0 010 1.5h-1a.75.75 0 01-.75-.75z" />
            </svg>
            Reasoning
          </span>
        )}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fill="currentColor"
          className={cx('w-3 h-3 ml-auto transition-transform', expanded ? 'rotate-180' : '')}
        >
          <path fillRule="evenodd" d="M4.22 6.22a.75.75 0 011.06 0L8 8.94l2.72-2.72a.75.75 0 111.06 1.06l-3.25 3.25a.75.75 0 01-1.06 0L4.22 7.28a.75.75 0 010-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {expanded && (
        <div className={cx('px-3 py-2 text-xs leading-relaxed border-t', tc.borderPrimary, tc.textMuted, 'whitespace-pre-wrap')}>
          {text}
        </div>
      )}
    </div>
  );
};

// ─── Streaming message (in-progress) ─────────────────────────────────────────

export const StreamingMessage: React.FC<{
  text: string;
  reasoning: string;
  currentTool?: ToolCall | null;
}> = ({ text, reasoning, currentTool }) => {
  const tc = useThemeClasses();

  return (
    <div className="flex justify-start mb-4 px-4">
      {/* Avatar */}
      <div className="flex-shrink-0 mr-2.5 mt-1">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white text-[10px] font-bold shadow-sm">
          AI
        </div>
      </div>

      <div className="flex flex-col max-w-[85%] min-w-0">
        {/* Reasoning block (collapsible, auto-expands while thinking) */}
        {reasoning && <ReasoningBlock text={reasoning} isStreaming={!text && !currentTool} />}

        {/* Current tool call — only the latest, stays visible even after text starts */}
        {currentTool && (
          <div
            className={cx(
              'mb-2 flex items-center gap-2 rounded-lg px-3 py-2 text-xs border',
              tc.borderPrimary,
              tc.bgTertiary,
            )}
          >
            {currentTool.status === 'running' ? (
              <span className="relative flex h-3 w-3 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" />
              </span>
            ) : currentTool.status === 'error' ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-red-500 flex-shrink-0">
                <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm2.78-4.22a.75.75 0 01-1.06 0L8 9.06l-1.72 1.72a.75.75 0 11-1.06-1.06L6.94 8 5.22 6.28a.75.75 0 011.06-1.06L8 6.94l1.72-1.72a.75.75 0 111.06 1.06L9.06 8l1.72 1.72a.75.75 0 010 1.06z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-green-500 flex-shrink-0">
                <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm3.844-8.791a.75.75 0 00-1.188-.918l-3.7 4.79-1.649-1.833a.75.75 0 10-1.114 1.004l2.25 2.5a.75.75 0 001.15-.043l4.25-5.5z" clipRule="evenodd" />
              </svg>
            )}
            <span className={cx('font-medium', tc.textPrimary)}>
              {currentTool.status === 'running' ? 'Calling' : 'Used'}{' '}
              <span className="font-mono bg-black/5 dark:bg-white/10 rounded px-1 py-0.5">
                {currentTool.name}
              </span>
            </span>
          </div>
        )}

        {/* Response text */}
        {text ? (
          <div
            className={cx(
              'rounded-2xl rounded-tl-sm px-3.5 py-2.5',
              tc.bgSecondary,
              tc.textPrimary,
              PROSE_CLASSES,
            )}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            {/* Blinking cursor */}
            <span className="inline-block w-1.5 h-4 ml-0.5 bg-current animate-pulse rounded-sm align-text-bottom opacity-60" />
          </div>
        ) : (currentTool) ? (
          /* Tool running — no response text yet, tool indicator is shown above */
          null
        ) : reasoning ? (
          /* Still thinking — no response text yet */
          <div className={cx('rounded-2xl rounded-tl-sm px-3.5 py-2.5', tc.bgSecondary)}>
            <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
            </span>
          </div>
        ) : (
          /* No reasoning, no text yet — typing indicator */
          <div className={cx('rounded-2xl rounded-tl-sm px-3.5 py-2.5', tc.bgSecondary)}>
            <span className="flex gap-1">
              <span className={cx('w-2 h-2 rounded-full animate-bounce', tc.textMuted, 'bg-current')} style={{ animationDelay: '0ms' }} />
              <span className={cx('w-2 h-2 rounded-full animate-bounce', tc.textMuted, 'bg-current')} style={{ animationDelay: '150ms' }} />
              <span className={cx('w-2 h-2 rounded-full animate-bounce', tc.textMuted, 'bg-current')} style={{ animationDelay: '300ms' }} />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Message bubble ──────────────────────────────────────────────────────────

export const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const tc = useThemeClasses();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── User message ─────────────────────────────────────────────────────────
  if (message.role === 'user') {
    return (
      <div className="flex justify-end mb-4 px-4">
        <div
          className={cx(
            'max-w-[80%] rounded-2xl rounded-tr-sm px-3.5 py-2.5',
            'bg-[#025a94] text-white',
            '[&_p]:text-sm [&_p]:leading-relaxed [&_p]:my-0',
          )}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }

  // ── Assistant message ────────────────────────────────────────────────────
  return (
    <div className="flex justify-start mb-4 px-4 group/msg">
      {/* Avatar */}
      <div className="flex-shrink-0 mr-2.5 mt-1">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white text-[10px] font-bold shadow-sm">
          AI
        </div>
      </div>

      <div className="flex flex-col max-w-[85%] min-w-0">
        {/* Reasoning */}
        {message.reasoning && <ReasoningBlock text={message.reasoning} />}

        {/* Content */}
        <div
          className={cx(
            'rounded-2xl rounded-tl-sm px-3.5 py-2.5',
            tc.bgSecondary,
            tc.textPrimary,
            PROSE_CLASSES,
          )}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-1 mt-1 opacity-0 group-hover/msg:opacity-100 transition-opacity">
          <button
            onClick={handleCopy}
            className={cx('p-1 rounded-md text-xs transition-colors', tc.textMuted, tc.hover)}
            title={copied ? 'Copied!' : 'Copy'}
          >
            {copied ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-green-500">
                <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 01.208 1.04l-5 7.5a.75.75 0 01-1.154.114l-3-3a.75.75 0 011.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 011.04-.207z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
                <path d="M5.5 3.5A1.5 1.5 0 017 2h4.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 01.439 1.061V9.5A1.5 1.5 0 0114 11h-.5V8.621a3 3 0 00-.879-2.121L10.5 4.379A3 3 0 008.379 3.5H5.5z" />
                <path d="M4 5a1.5 1.5 0 00-1.5 1.5v6A1.5 1.5 0 004 14h5a1.5 1.5 0 001.5-1.5V8.621a1.5 1.5 0 00-.44-1.06L7.94 5.439A1.5 1.5 0 006.878 5H4z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
